import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type { ComponentProps, ReactNode } from 'react';
import CancelOutline from '~icons/material-symbols/cancel-outline';
import CheckCircleOutline from '~icons/material-symbols/check-circle-outline';
import ErrorOutline from '~icons/material-symbols/error-outline';

export type AlertType = 'success' | 'warning' | 'error';

const ICON_SIZE_CLASSNAME = '!size-[120px]';

const iconMap: Record<AlertType, ReactNode> = {
  success: <CheckCircleOutline className={ICON_SIZE_CLASSNAME} />,
  warning: <ErrorOutline className={ICON_SIZE_CLASSNAME} />,
  error: <CancelOutline className={ICON_SIZE_CLASSNAME} />,
};

const ringClassMap: Record<AlertType, string> = {
  success: 'border-green-200 text-green-400',
  warning: 'border-yellow-200 text-yellow-500',
  error: 'border-red-200 text-red-500',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: AlertType;
  title?: ReactNode;
  message: ReactNode;
  size?: ComponentProps<typeof AlertDialogContent>['size'];
  footerContent?: ReactNode;
  className?: string;
}

export function CommonAlertModal({
  open,
  type,
  title,
  message,
  size = 'default',
  onOpenChange,
  footerContent,
  className,
}: Props) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent size={size} className={cn('sm:max-w-xl', className)}>
        <AlertDialogHeader className="flex flex-col items-center gap-6">
          <AlertDialogMedia
            className={cn(
              'mx-auto',
              'mb-0 bg-transparent',
              'flex items-center justify-center',
              'sm:col-auto sm:row-auto',
              ringClassMap[type],
            )}
          >
            {iconMap[type]}
          </AlertDialogMedia>
          <AlertDialogTitle
            className={cn(
              title
                ? 'w-full text-center text-lg font-semibold text-gray-700'
                : 'sr-only',
            )}
          >
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="w-full text-center text-lg font-semibold text-gray-600">
            {message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-end">
          {footerContent}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
